import { CSSProperties, useMemo } from "react";
import { useIsNarrow } from "utils/useMediaQuery";
import { DashboardWidgetFrame } from "./DashboardWidgetFrame";
import {
    DashboardConfig,
    DEFAULT_GRID_COLUMNS,
    DEFAULT_GRID_GAP,
    DEFAULT_GRID_ROW_HEIGHT,
} from "./types";
// The dashboard grid + widget-body stylesheet is loaded globally from App.tsx
// (see operator-task-container-configurable-dashboards) so it is present in both
// the standalone /dashboard route chunk and the embedded container-screen chunk
// that render this grid.

/**
 * Presentational dashboard widget grid.
 *
 * Renders a {@link DashboardConfig}'s widgets as a config-driven CSS grid of
 * {@link DashboardWidgetFrame}s. Extracted from {@link DashboardView} so the same
 * rendering primitives can be reused wherever a dashboard's widgets are shown
 * inline (e.g. the container-screen dashboard tab bar) without navigating to the
 * dashboard viewer or duplicating the grid layout logic.
 *
 * On narrow (mobile) viewports the multi-column grid collapses to a single column
 * so every widget stacks full-width in row order (DashboardWidgetFrame drops its
 * per-widget col/row placement to match).
 *
 * Renders nothing when the config has no widgets; callers own any "empty" copy.
 */
export const DashboardGrid = ({ config }: { config: DashboardConfig | null | undefined }) => {
    const isNarrow = useIsNarrow();
    const widgets = useMemo(() => config?.widgets ?? [], [config?.widgets]);

    const gridStyle: CSSProperties = {
        gridTemplateColumns: isNarrow
            ? "1fr"
            : `repeat(${config?.grid?.columns ?? DEFAULT_GRID_COLUMNS}, 1fr)`,
        gridAutoRows: `${config?.grid?.rowHeight ?? DEFAULT_GRID_ROW_HEIGHT}px`,
        gap: `${config?.grid?.gap ?? DEFAULT_GRID_GAP}px`,
    };

    if (widgets.length === 0) {
        return null;
    }

    return (
        <div className="DashboardView__grid" style={gridStyle}>
            {widgets.map((widget) => (
                <DashboardWidgetFrame key={widget.uid} widget={widget} singleColumn={isNarrow} />
            ))}
        </div>
    );
};

export default DashboardGrid;
